// Catálogo de Torneios do painel admin.
//
// Criar tem dois caminhos:
//   - "Selecionar jogos"       → POST /admin/tournaments/from-games
//   - "Copiar edição inteira"  → POST /admin/tournaments/from-edicao/:id
// Nos dois o backend COPIA os jogos para linhas novas, isolando o ranking do torneio do
// ranking do jogo original.
//
// Editar é só de metadados (nome, descrição, datas, multiplicador): trocar os itens de um
// torneio já criado está fora do escopo da v1.
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import moment from 'moment';
import {
  createTorneioFromEdicao,
  createTorneioFromGames,
  listEdicoes,
  listTorneios,
  updateTorneio,
  type EdicaoAdmin,
  type PickableGame,
  type TorneioListItem,
  type TorneioStatus,
  type TorneioType,
} from '../../services/admin';
import { TorneioGamePicker } from './TorneioGamePicker';

const { TextArea } = Input;

const STATUS_TAG: Record<TorneioStatus, { color: string; label: string }> = {
  futuro: { color: 'blue', label: 'futuro' },
  ativo: { color: 'green', label: 'ativo' },
  encerrado: { color: 'default', label: 'encerrado' },
};

const fmt = (iso: string): string => moment(iso).format('DD/MM/YYYY HH:mm');

/** Rótulos do select (copy de UI). Os VALORES são o contrato da API. */
const TORNEIO_TYPE_LABELS: Record<TorneioType, string> = {
  golden_owl: 'Coruja dourada',
  purple_trophy: 'Troféu roxo',
  green_trophy: 'Troféu verde',
};

interface FormValues {
  name: string;
  description?: string;
  periodo: [moment.Moment, moment.Moment];
  multiplier: number;
  /** Visual do torneio no app (troféu + cor). Vazio = sem tipo → troféu roxo. */
  type?: TorneioType;
}

type ItemSource = 'games' | 'edicao';

/** Erros do módulo de torneios vêm no formato padrão do Nest (`{ message }`). */
function errorMessage(err: unknown): string | undefined {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
}

export const TorneiosCatalog: React.FC = () => {
  const [torneios, setTorneios] = useState<TorneioListItem[]>([]);
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TorneioListItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState<ItemSource>('games');
  const [selectedGames, setSelectedGames] = useState<PickableGame[]>([]);
  const [edicaoId, setEdicaoId] = useState<number | undefined>(undefined);
  const [form] = Form.useForm<FormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTorneios(await listTorneios());
    } catch {
      message.error('Falha ao carregar os torneios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    listEdicoes()
      .then(setEdicoes)
      .catch(() => undefined);
  }, [load]);

  const openCreate = (): void => {
    setEditing(null);
    setSource('games');
    setSelectedGames([]);
    setEdicaoId(undefined);
    form.resetFields();
    form.setFieldsValue({ multiplier: 1 });
    setModalOpen(true);
  };

  const openEdit = (t: TorneioListItem): void => {
    setEditing(t);
    form.setFieldsValue({
      name: t.name,
      description: t.description ?? undefined,
      periodo: [moment(t.startsAt), moment(t.endsAt)],
      multiplier: t.multiplier,
      type: t.type ?? undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    let v: FormValues;
    try {
      v = await form.validateFields();
    } catch {
      return; // o form já exibiu os erros
    }
    const meta = {
      name: v.name.trim(),
      description: v.description?.trim() || undefined,
      startsAt: v.periodo[0].toISOString(),
      endsAt: v.periodo[1].toISOString(),
      multiplier: v.multiplier,
      type: v.type,
    };

    setSaving(true);
    try {
      if (editing) {
        await updateTorneio(editing.id, meta);
        message.success('Torneio atualizado.');
      } else if (source === 'edicao') {
        if (edicaoId === undefined) {
          message.error('Escolha uma edição.');
          return;
        }
        await createTorneioFromEdicao(edicaoId, meta);
        message.success('Torneio criado a partir da edição.');
      } else {
        if (selectedGames.length === 0) {
          message.error('Selecione ao menos um jogo.');
          return;
        }
        const items = selectedGames.map((g) =>
          g.kind === 'passatempo' ? { passatemposId: g.id } : { jogosId: g.id },
        );
        await createTorneioFromGames({ ...meta, items });
        message.success('Torneio criado.');
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      message.error(errorMessage(err) ?? 'Falha ao salvar o torneio.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    { title: 'Nome', dataIndex: 'name' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: TorneioStatus) => <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].label}</Tag>,
    },
    { title: 'Início', dataIndex: 'startsAt', width: 150, render: fmt },
    { title: 'Fim', dataIndex: 'endsAt', width: 150, render: fmt },
    { title: 'Mult.', dataIndex: 'multiplier', width: 70, render: (n: number) => `${n}x` },
    { title: 'Jogos', dataIndex: 'itemsCount', width: 80 },
    { title: 'Inscritos', dataIndex: 'participantsCount', width: 90 },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_: unknown, t: TorneioListItem) => (
        <Button size="small" onClick={() => openEdit(t)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Torneios</h2>
        <Button type="primary" onClick={openCreate}>
          Novo torneio
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={torneios}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editing ? `Editar torneio #${editing.id}` : 'Novo torneio'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
        width={720}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome.' }]}
          >
            <Input placeholder="Ex.: Torneio de Verão" />
          </Form.Item>
          <Form.Item name="description" label="Descrição">
            <TextArea rows={2} />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item
              name="periodo"
              label="Período (início → fim)"
              rules={[{ required: true, message: 'Informe o período.' }]}
            >
              <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="multiplier" label="Multiplicador" rules={[{ required: true }]}>
              <InputNumber min={1} />
            </Form.Item>
            {/* Visual do torneio no app. Vazio = sem tipo → o app cai no troféu roxo. */}
            <Form.Item name="type" label="Tipo (troféu)">
              <Select allowClear placeholder="Sem tipo" style={{ width: 200 }}>
                {(Object.keys(TORNEIO_TYPE_LABELS) as TorneioType[]).map((t) => (
                  <Select.Option key={t} value={t}>
                    {TORNEIO_TYPE_LABELS[t]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          {!editing && (
            <>
              <Form.Item label="Jogos do torneio">
                <Radio.Group value={source} onChange={(e) => setSource(e.target.value)}>
                  <Radio.Button value="games">Selecionar jogos</Radio.Button>
                  <Radio.Button value="edicao">Copiar edição inteira</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {source === 'games' ? (
                <TorneioGamePicker value={selectedGames} onChange={setSelectedGames} />
              ) : (
                <Form.Item label="Edição de origem">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    style={{ width: 320 }}
                    placeholder="Escolha a edição"
                    value={edicaoId}
                    onChange={(v) => setEdicaoId(v)}
                  >
                    {edicoes.map((e) => (
                      <Select.Option key={e.id} value={e.id}>
                        {e.nome ?? `Edição #${e.id}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </>
          )}

          {editing && (
            <p style={{ color: '#999' }}>
              Edição de itens (trocar jogos) não é suportada nesta versão — só metadados.
            </p>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TorneiosCatalog;
